import {
  makeStyles,
  tokens,
} from '@fluentui/react-components';

const screenSmall = '768px';
const screenMedium = '978px';

export const useUserInvitationSelectionPageStyles = makeStyles({
  idpSelectionPage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    minHeight: '100vh',
    height: 'auto',
    backgroundImage: 'radial-gradient(at 2.7% 92.9%, hsl(200, 35%, 93%) 0px, transparent 50%),radial-gradient(at 100.2% 77.6%, hsl(200, 35%, 93%) 0px, transparent 50%),radial-gradient(at 1.3% 13.0%, #f0f3f5 0px, transparent 50%),radial-gradient(at 55.5% 13.8%, #fcf9f5 0px, transparent 50%),radial-gradient(at 55.7% 72.1%, #ffffff 0px, transparent 50%),radial-gradient(at 96.0% 12.2%, hsl(200, 35%, 93%) 0px, transparent 50%)',
    [`@media (max-width: ${screenSmall})`]: {
      backgroundImage: 'none',
    },
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    margin: 'auto',
    [`@media (max-width: ${screenSmall})`]: {
      margin: '0',
      width: '100%',
    },
  },
  section: {
    display: 'flex',
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow8,
    [`@media (max-width: ${screenSmall})`]: {
      boxShadow: 'none',
    },
  },
  identitySection: {
    width: '1070px',
    maxWidth: '100%',
    height: '640px',
    boxSizing: 'border-box',
    // Collapse to a responsive width at the same breakpoint where the right-side preview
    // (contentRight) is hidden, otherwise the fixed 1070px card overflows and leaves an empty
    // right area between 769px and 978px.
    [`@media (max-width: ${screenMedium})`]: {
      width: '100%',
      height: 'auto',
    },
  },
  sectionAdditionalInfo: {
    display: 'block',
    textAlign: 'center',
    boxSizing: 'border-box',
    // Stretch the "Not sure? Get help here." card to the full width of the card above it (the flex
    // column centers its children by default, which otherwise left this footer at the narrow
    // left-column width). See bugs 10916553 / 10916600.
    alignSelf: 'stretch',
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
  },
  helpFooterContent: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
  },
  helpFooterIcon: {
    fontSize: '16px',
    color: tokens.colorNeutralForeground3,
  },
  // Keep the standard "Get help here." Fluent Button inline and compact next to "Not sure?".
  helpFooterButton: {
    minWidth: 'auto',
    fontSize: tokens.fontSizeBase300,
  },
  contentLeft: {
    display: 'flex',
    flexDirection: 'column',
    // border-box so the 480px includes the horizontal padding; combined with the 590px right
    // column (550px image + 20px padding each side) this sums to exactly the 1070px card width.
    boxSizing: 'border-box',
    width: '480px',
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
    paddingLeft: tokens.spacingHorizontalXXXL,
    paddingRight: tokens.spacingHorizontalXXXL,
    [`@media (max-width: ${screenSmall})`]: {
      width: '100%',
      paddingBottom: '0',
    },
  },
  textAndButtons: {
    // Fill (and never exceed) the fixed-width left column. Without an explicit width the flex
    // item shrink-wraps to its widest child — e.g. the single-line AAD-only info MessageBar —
    // which pushed the button and message bar past the column and over the right-side image.
    width: '100%',
    boxSizing: 'border-box',
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  title: {
    marginTop: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalS,
  },
  invitationMeta: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  invitationBackLink: {
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
  },
  invitationBackIcon: {
    fontSize: '16px',
  },
  invitationPill: {
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusCircular,
    borderTopWidth: tokens.strokeWidthThin,
    borderRightWidth: tokens.strokeWidthThin,
    borderBottomWidth: tokens.strokeWidthThin,
    borderLeftWidth: tokens.strokeWidthThin,
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorStatusSuccessForeground3,
    borderRightColor: tokens.colorStatusSuccessForeground3,
    borderBottomColor: tokens.colorStatusSuccessForeground3,
    borderLeftColor: tokens.colorStatusSuccessForeground3,
    color: tokens.colorStatusSuccessForeground1,
    backgroundColor: tokens.colorStatusSuccessBackground1,
  },
  invitationPillDanger: {
    borderTopColor: tokens.colorStatusDangerForeground3,
    borderRightColor: tokens.colorStatusDangerForeground3,
    borderBottomColor: tokens.colorStatusDangerForeground3,
    borderLeftColor: tokens.colorStatusDangerForeground3,
    color: tokens.colorStatusDangerForeground1,
    backgroundColor: tokens.colorStatusDangerBackground1,
  },
  invitationPillIcon: {
    fontSize: '12px',
  },
  choiceList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalXXL,
    marginBottom: tokens.spacingVerticalXXL,
  },
  choiceButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    columnGap: tokens.spacingHorizontalM,
    width: '100%',
    height: 'auto',
    textAlign: 'left',
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
  },
  choiceButtonIcon: {
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: '24px',
    color: tokens.colorNeutralForeground2,
  },
  choiceButtonText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    rowGap: tokens.spacingVerticalXXS,
  },
  choiceButtonTitle: {
    color: tokens.colorNeutralForeground1,
  },
  choiceButtonDescription: {
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'normal',
  },
  crossLink: {
    marginTop: tokens.spacingVerticalL,
    textAlign: 'center',
  },
  aadInfoMessage: {
    marginTop: tokens.spacingVerticalL,
  },
  buttons: {
    marginTop: tokens.spacingVerticalXXL,
    marginBottom: tokens.spacingVerticalXXL,
  },
  contentRight: {
    lineHeight: '1',
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    paddingTop: tokens.spacingVerticalXL,
    paddingBottom: tokens.spacingVerticalXL,
    [`@media (max-width: ${screenMedium})`]: {
      display: 'none',
    },
  },
});
